import React, { useState, useMemo } from "react";
import {
  Card,
  Row,
  Col,
  Tag,
  Statistic,
  Button,
  Icon,
  Modal,
  notification,
  DatePicker,
  Input,
  Collapse,
} from "antd";
import moment from "moment";
import AnimateHeight from "react-animate-height";
import api from "../../../../services/api";
import AcordoInfos from "./AcordoInfos";
import AcordoParcelas from "./AcordoParcelas";
import * as MoneyFormater from "../../../../utils/MoneyFormater";
import "./styles.css";

const AcordoView = ({ acordo, onUpdate }) => {
  const [expanded, setExpanded] = useState(false);
  const [statusChanging, setStatusChanging] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editData, setEditData] = useState({
    novo_numero_contrato: "",
    novo_produto_carteira: "",
    notes: "",
  });
  const [savingEdit, setSavingEdit] = useState(false);

  // Calcular estatísticas do acordo
  const stats = useMemo(() => {
    const parcelas = acordo.parcela_set || [];

    const totalParcelas = parcelas.length;
    const parcelasPagas = parcelas.filter(
      (p) => p.status_parcela === "1"
    ).length;
    const parcelasPendentes = totalParcelas - parcelasPagas;

    const valorTotal = parcelas.reduce(
      (sum, p) => sum + (parseFloat(p.valor_parcela) || 0),
      0
    );
    const valorPago = parcelas
      .filter((p) => p.status_parcela === "1")
      .reduce((sum, p) => sum + (parseFloat(p.valor_pago) || 0), 0);
    const valorPendente = valorTotal - valorPago;

    return {
      totalParcelas,
      parcelasPagas,
      parcelasPendentes,
      valorTotal,
      valorPago,
      valorPendente,
    };
  }, [acordo]);

  // Determinar cor do status
  const getStatusColor = (status) => {
    const statusLower = String(status || "").toLowerCase();
    if (statusLower.includes("ativo") || statusLower.includes("quitado")) {
      return "green";
    }
    if (statusLower.includes("quebrado") || statusLower.includes("cancelado")) {
      return "red";
    }
    return "orange";
  };

  // Handler para alterar status
  const handleAlterarStatus = () => {
    let statusSelected = null;
    let dataQuebra = null;
    let isQuebra = false;

    const modal = Modal.confirm({
      title: "Alterar status do acordo",
      width: 500,
      okText: "Confirmar",
      cancelText: "Cancelar",
      icon: null,
      content: (
        <div style={{ margin: "15px 0" }}>
          <label
            style={{ display: "block", marginBottom: "8px", fontWeight: 500 }}
          >
            Selecione o status do acordo:
          </label>
          <select
            className="ant-input"
            style={{ marginBottom: "12px", width: "100%" }}
            onChange={(e) => {
              statusSelected = e.target.value;
              isQuebra = statusSelected.includes("Quebrado");
            }}
            defaultValue="0"
          >
            <option value="0">Selecione</option>
            <option value="ATIVO">Ativo</option>
            <option value="Cancelado">Cancelado</option>
            <option value="Quebrado - Erro HDI">Quebrado - Erro HDI</option>
            <option value="Quebrado - Não pagamento">
              Quebrado - Não pagamento
            </option>
            <option value="Reacordo">Reacordo</option>
            <option value="Quitado">Quitado</option>
          </select>
          <label
            style={{ display: "block", marginBottom: "8px", fontWeight: 500 }}
          >
            Data da quebra (opcional):
          </label>
          <DatePicker
            format="DD/MM/YYYY"
            placeholder="Data da quebra"
            onChange={(date) => {
              dataQuebra = date ? date.format("YYYY-MM-DD") : null;
            }}
            style={{ width: "100%" }}
          />
        </div>
      ),
      onOk: async () => {
        if (!statusSelected || statusSelected === "0") {
          Modal.error({
            title: "Erro",
            content: "Selecione um status.",
          });
          return Promise.reject();
        }

        if (isQuebra && !dataQuebra) {
          Modal.error({
            title: "Erro",
            content: "Quando o status é uma quebra você PRECISA setar a data.",
          });
          return Promise.reject();
        }

        if (!isQuebra && dataQuebra) {
          Modal.error({
            title: "Erro",
            content: "Se o status não é uma quebra NÃO preencha a data.",
          });
          return Promise.reject();
        }

        setStatusChanging(true);

        try {
          await api.patch(`/acordo/acordos/${acordo.id}/`, {
            status_acordo: statusSelected,
            data_quebra: dataQuebra,
          });

          notification.success({
            message: "Status do acordo atualizado com sucesso.",
          });

          if (onUpdate) onUpdate();
        } catch (err) {
          Modal.error({
            title: "Erro",
            content: "Não foi possível atualizar o status do acordo.",
          });
        } finally {
          setStatusChanging(false);
        }
      },
    });
  };

  // Handler para excluir acordo (Hard Delete)
  const handleExcluirAcordo = () => {
    Modal.confirm({
      title: "Excluir este acordo?",
      content:
        "Tem certeza que deseja excluir permanentemente este acordo? Esta ação não pode ser desfeita.",
      okText: "Excluir",
      okType: "danger",
      cancelText: "Cancelar",
      onOk: async () => {
        try {
          await api.delete(`/acordo/acordos/${acordo.id}/`);

          notification.success({
            message: "Acordo excluído com sucesso.",
          });

          if (onUpdate) onUpdate();
        } catch (err) {
          Modal.error({
            title: "Erro ao excluir",
            content: "Não foi possível excluir o acordo.",
          });
        }
      },
    });
  };

  const handleOpenEditModal = () => {
    setEditData({
      novo_numero_contrato: acordo.novo_numero_contrato || "",
      novo_produto_carteira: acordo.novo_produto_carteira || "",
      notes: acordo.notes || "",
    });
    setEditModalVisible(true);
  };

  const handleSaveEdit = async () => {
    setSavingEdit(true);
    try {
      await api.patch(`/acordo/acordos/${acordo.id}/`, editData);
      notification.success({ message: "Informações atualizadas!" });
      setEditModalVisible(false);
      if (onUpdate) onUpdate();
    } catch (error) {
      notification.error({ message: "Erro ao atualizar informações." });
    } finally {
      setSavingEdit(false);
    }
  };

  return (
    <Card className="acordo__card">
      {/* HEADER - Sempre visível */}
      <div className="acordo__header">
        <Row gutter={16} align="middle">
          <Col xs={24} md={7}>
            <div className="acordo__basic-info">
              <div className="acordo__title-line">
                <h3 className="acordo__nome">
                  {acordo.nome || acordo.pasta || "Acordo"}
                </h3>

                {/* TAG GANHO (se existir) */}
                {acordo.ganho && (
                  <Tag className="acordo__chip" color="gold">
                    GANHO
                  </Tag>
                )}

                {/* STATUS ACORDO – compacto, ao lado do título */}
                <Tag
                  className="acordo__status-tag"
                  color={getStatusColor(acordo.status_acordo)}
                >
                  {acordo.status_acordo || "N/A"}
                </Tag>
              </div>

              {/* Tipo de acordo embaixo, como “subtag” */}
              {acordo.tipo_acordo && (
                <div className="acordo__tags">
                  <Tag className="acordo__chip" color="blue">
                    {acordo.tipo_acordo}
                  </Tag>
                </div>
              )}

              {/* Informação do Negociador em Destaque */}
              <div style={{ marginTop: 8, color: '#595959', fontSize: '14px', display: 'flex', alignItems: 'center' }}>
                <Icon type="user" style={{ marginRight: 6, color: '#1890ff' }} />
                <span>
                  Negociador: <strong>{acordo.operador_nome || acordo.operador_username || "-"}</strong>
                </span>
              </div>
            </div>
          </Col>

          <Col xs={24} md={13}>
            <Row gutter={8}>
              <Col span={8}>
                <Statistic
                  title="Valor Pendente"
                  value={stats.valorPendente}
                  prefix="R$"
                  precision={2}
                  valueStyle={{ color: "#cf1322", fontSize: 16 }}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="Parcelas Pendentes"
                  value={`${stats.parcelasPendentes}/${stats.totalParcelas}`}
                  valueStyle={{ color: "#fa8c16", fontSize: 16 }}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="Valor Pago"
                  value={stats.valorPago}
                  prefix="R$"
                  precision={2}
                  valueStyle={{ color: "#3f8600", fontSize: 16 }}
                />
              </Col>
            </Row>
          </Col>

          <Col xs={24} md={4} style={{ textAlign: "right" }}>
            <Button onClick={() => setExpanded(!expanded)}>
              {expanded ? "Ocultar" : "Detalhes"}
              <Icon type={expanded ? "up" : "down"} />
            </Button>
          </Col>
        </Row>
      </div>

      {/* ÁREA EXPANSÍVEL */}
      <AnimateHeight duration={350} height={expanded ? "auto" : 0}>
        <div className="acordo__content">
          {/* Cards Financeiros */}
          <Row gutter={16} className="acordo__financial-cards">
            <Col xs={12} md={6}>
              <Card
                size="small"
                className="acordo__stat-card"
                style={{ borderRadius: "5px" }}
              >
                <Statistic
                  title="Valor Original"
                  value={acordo.valor_original_da_divida || 0}
                  prefix="R$"
                  precision={2}
                />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card
                size="small"
                className="acordo__stat-card"
                style={{ borderRadius: "5px" }}
              >
                <Statistic
                  title="Desconto"
                  value={acordo.percentual_de_desconto || 0}
                  suffix="%"
                  precision={2}
                />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card
                size="small"
                className="acordo__stat-card"
                style={{ borderRadius: "5px" }}
              >
                <Statistic
                  title="Valor Final"
                  value={acordo.valor_acordo || 0}
                  prefix="R$"
                  precision={2}
                />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card
                size="small"
                className="acordo__stat-card"
                style={{ borderRadius: "5px" }}
              >
                <Statistic title="Total Parcelas" value={stats.totalParcelas} />
              </Card>
            </Col>
          </Row>

          {/* AcordoInfos */}
          <AcordoInfos acordo={acordo} onUpdate={onUpdate} />

          {/* AcordoContratos (Rateio) */}
          {acordo.contrato_set && acordo.contrato_set.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <Collapse
                bordered={false}
                expandIcon={({ isActive }) => <Icon type="caret-right" rotate={isActive ? 90 : 0} />}
              >
                <Collapse.Panel header="Rateios (Contratos Vinculados)" key="1" style={{ background: '#f7f7f7', borderRadius: 4, marginBottom: 24, border: 0, overflow: 'hidden' }}>
                  <Row gutter={[16, 16]}>
                    {acordo.contrato_set.map((contrato, idx) => (
                      <Col xs={24} sm={12} md={8} key={idx}>
                        <Card size="small" style={{ borderRadius: "5px" }}>
                          <p style={{ margin: 0 }}>
                            <strong>Contrato:</strong> {contrato.contrato}
                          </p>
                          <p style={{ margin: 0 }}>
                            <strong>Carteira/Produto:</strong> {contrato.carteira_produto}
                          </p>
                          <p style={{ margin: 0 }}>
                            <strong>Valor Original:</strong> R$ {MoneyFormater.format(contrato.valor_do_contrato || 0)}
                          </p>
                          <p style={{ margin: 0 }}>
                            <strong>Percentual:</strong> {contrato.percentual}%
                          </p>
                          <p style={{ margin: 0 }}>
                            <strong>Valor no Acordo:</strong> R$ {MoneyFormater.format(contrato.valor_total || 0)}
                          </p>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                </Collapse.Panel>
              </Collapse>
            </div>
          )}

          {/* Tabela de Parcelas */}
          <AcordoParcelas
            parcelas={acordo.parcela_set || []}
            acordoId={acordo.id}
            onUpdate={onUpdate}
          />

          {/* Ações do Acordo */}
          <div className="acordo__actions">
            <Button
              type="primary"
              ghost
              icon="edit"
              onClick={handleOpenEditModal}
              style={{ marginRight: 8 }}
            >
              Editar Informações
            </Button>
            <Button
              type="default"
              icon="sync"
              onClick={handleAlterarStatus}
              loading={statusChanging}
            >
              Alterar Status
            </Button>
            <Button type="danger" icon="delete" onClick={handleExcluirAcordo}>
              Excluir Acordo
            </Button>
          </div>
        </div>
      </AnimateHeight>

      <Modal
        title="Editar Observações do Acordo"
        visible={editModalVisible}
        onOk={handleSaveEdit}
        onCancel={() => setEditModalVisible(false)}
        confirmLoading={savingEdit}
      >
        <div>
          <label style={{ fontWeight: 500 }}>Observações (Notes):</label>
          <Input.TextArea
            rows={6}
            value={editData.notes}
            onChange={(e) =>
              setEditData({ ...editData, notes: e.target.value })
            }
          />
        </div>
      </Modal>
    </Card>
  );
};

export default AcordoView;



// WEBPACK FOOTER //
// ./src/pages/Pasta/Acordos/AcordoView/index.jsx